import React from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useNavigate } from 'react-router-dom';
import { StatusOverview, UtilityStatus } from '../../../types/utility.types';

interface StatusOverviewChartProps {
  data: StatusOverview[];
}

const getStatusColor = (status: UtilityStatus): string => {
  switch (status) {
    case 'LIVE':
      return '#4caf50'; // success green
    case 'PENDING':
      return '#ff9800'; // warning orange
    case 'TERMINATED':
      return '#f44336'; // error red
    default:
      return '#9e9e9e'; // default grey
  }
};

const StatusOverviewChart: React.FC<StatusOverviewChartProps> = ({ data }) => {
  const navigate = useNavigate();

  // Ensure unique IDs by adding index to status
  const chartData = data.map((item, index) => ({
    id: index,  // Use index as unique ID
    value: item.count,
    label: item._id.status.charAt(0).toUpperCase() + item._id.status.slice(1).toLowerCase(),
    color: getStatusColor(item._id.status.toUpperCase() as UtilityStatus),
    originalStatus: item._id.status
  }));

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
        Click on a legend item to view contracts
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', height: 300 }}>
        <Box sx={{ flex: 1, height: '100%' }}>
          <PieChart
            series={[
              {
                data: chartData,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30 },
              },
            ]}
            height={300}
            margin={{ right: 20 }}
            legend={{ hidden: true }}
            sx={{
              '& .MuiPopper-root': {
                '& .MuiChartsTooltip-root': {
                  background: 'rgba(30, 41, 59, 0.95) !important',
                  backdropFilter: 'blur(12px) !important',
                  WebkitBackdropFilter: 'blur(12px) !important',
                  border: '1px solid rgba(255, 255, 255, 0.1) !important',
                  borderRadius: '8px !important',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3) !important',
                },
                '& .MuiChartsTooltip-table': {
                  color: 'rgba(255, 255, 255, 0.9) !important',
                },
                '& .MuiChartsTooltip-cell': {
                  color: 'rgba(255, 255, 255, 0.9) !important',
                }
              }
            }}
          />
        </Box>
        <Box sx={{ 
          width: 120, 
          ml: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1
        }}>
          {chartData.map((item, index) => (
            <Box
              key={item.id}
              onClick={() => {
                console.log('Manual legend click:', item);
                const statusParam = item.originalStatus;
                console.log('Navigating to:', `/utilities/contracts?status=${encodeURIComponent(statusParam)}`);
                navigate(`/utilities/contracts?status=${encodeURIComponent(statusParam)}`);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '2px 4px',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  backgroundColor: item.color,
                  borderRadius: '2px',
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ 
                fontSize: 12, 
                color: 'white',
                lineHeight: 1.2,
                fontWeight: 400
              }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export { StatusOverviewChart };
